import { Link } from "gatsby"
import React, { FC, ReactElement } from "react"
import InlineCode from "../../components/inlineCode"
import Layout from "../../components/layout"
import { CodeExample } from "../../components/validatorFuncs"

const forEachExample = `var validator = new ValidatorBuilder<Employee>()
    .ForEach(
        e => e.Skills,
        builder => 
        {
            builder.NotNull().MinLength(3);
        })
    .Build();
    
// this will produce two broken rules
var result = validator.Validate(new Employee 
    { 
        Skills = new[] { "Dev", "No", "Junk", null }
    });

foreach (var rule in result.BrokenRules)
{
    Console.WriteLine($"[{rule.Rule}] {rule.Key} {rule.Message}")
}

// output
// [MinLength] e.Skills[1] Value must have minimum length of 3
// [NotNull] e.Skills[3] Value cannot be null`

const messageExample1 = `var validator = new ValidatorBuilder<string>()
    .StartsWith("d")
    .EndsWith("m")
    .Email()
    .Message("This is not a drum")
    .Build();
    
// this will produce two broken rules (StartsWith,Email)
var result = validator.Validate("everything is a drum");

foreach (var rule in result.BrokenRules)
{
    Console.WriteLine($"[{rule.Rule}]: {rule.Message}")
}

// output
// [StartsWith,Email]: This is not a drum`

const messageExample2 = `var validator = new ValidatorBuilder<string>()
    .StartsWith("d")
    .EndsWith("m")
    .Email()
    // access the context to build the message
    .Message(ctx => $"This is not a drum ({ctx.BrokenRules.Count})")
    .Build();
    
// this will produce two broken rules (StartsWith,Email)
var result = validator.Validate("everything is a drum");

foreach (var rule in result.BrokenRules)
{
    Console.WriteLine($"[{rule.Rule}]: {rule.Message}")
}

// output
// [StartsWith,Email]: This is not a drum (2)`

function UtilityFunctions(): ReactElement {
  return (
    <Layout>
      <h1>Utility Functions</h1>
      <p>
        These functions can be used with any type when building validation
        functions.
      </p>
      <p>
        <ul className="list">
          {["ForEach", "Message"].map((func) => (
            <li>
              <Link to={`/functions/utility/#${func}`}>{func}</Link>
            </li>
          ))}
        </ul>
      </p>
      <hr className="my-6" />
      <UtilityMethod
        name="ForEach"
        description="Adds a validator to validate all items in a collection from the target of the selector expression."
        methods={[
          "ForEach<T, P>(Expression<Func<T, IEnumerable<P>>> selector, Action<IValidatorBuilder<P>> func)",
        ]}
      >
        <CodeExample code={forEachExample} />
      </UtilityMethod>
      <hr className="my-8" />
      <UtilityMethod
        name="Message"
        description="Aggregates broken rules into a single broken rule with the specified message. 
        This function should be added last and only once to the builder to avoid strange results."
        methods={[
          "Message<T>(string message)",
          "Message<T>(Func<ValidationContext<T>, string> func)",
        ]}
      >
        <CodeExample code={messageExample1} label="Using message string" />
        <CodeExample
          code={messageExample2}
          label="Using function returning string"
        />
      </UtilityMethod>
    </Layout>
  )
}

export default UtilityFunctions

type UtilityMethodProps = {
  name: string
  description: string | JSX.Element
  methods: string[]
}

const UtilityMethod: FC<UtilityMethodProps> = ({
  name,
  description,
  methods,
  children,
}) => (
  <div className="mb-4" id={name}>
    <h2 className="font-semibold">{name}</h2>
    <p className="my-4">{description}</p>
    <h5 className="font-semibold text-gray-800 text-sm">Method</h5>
    <div className="my-4">
      {methods.map((method) => (
        <>
          <InlineCode className="text-sm text-blue-600 bg-blue-100 font-semibold">
            {method}
          </InlineCode>
          <br />
        </>
      ))}
    </div>
    <h5 className="font-semibold text-gray-800 text-sm">Example Usage</h5>
    {children}
  </div>
)
